// components/Finale.tsx — rich, inline "Finale" completion panel.
//
// Rendered by App.tsx INLINE in the story log when `state.gameOver !== null`
// ("win" | "death"), replacing the old one-line endCard. It sits BELOW the
// finale prose (which already streamed into the log on the winning turn —
// Phase 1 server work) — this panel is the actionable surface, not the
// prose.
//
// This is a PRESENTATIONAL component: it owns no data fetching. App.tsx
// passes the story identity, the resolved scenario theme tokens, and the
// action callbacks. The panel wires into EXISTING infra via those callbacks:
//   - "Read your book"          → onReadBook()      → App opens BookReader.
//   - "Continue from a point"   → onContinueFromPoint() → App opens this
//                                  story's BookReader (per-chapter ForkAction
//                                  lives in the reader; self-fork allowed).
//   - "Share your story"        → onShare()         → App opens ShareModal,
//                                  PLUS a Facebook web-intent button built
//                                  here from the share URL.
//   - "Restart" / "New story"   → onRestart() / onNewStory().
//
// FORWARD-COMPAT SEAMS (intentionally inert in 2a):
//   - sequelOptions / onBeginSequel — Phase 3 fills the list from the
//     backend; App.tsx passes [] / a no-op (or "coming soon") handler today.
//     The layout + props are final so Phase 3 needs zero rework here.
//   - shareBlurb — Phase 2b passes a generated excerpt/foreword; the FB +
//     copy share text uses it when present, else falls back to the title.
//
// Inline styles only (project convention — no CSS framework). Theme tokens
// mirror ShareModal / BookReader resolution.

import { useCallback, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";

/**
 * A single "next book" option card. Phase 3 backend produces these; in 2a
 * the prop is absent/empty and the section renders nothing.
 */
export interface SequelOption {
  id: string;
  /** Structure tag, e.g. "three-act", "noir mystery". Rendered as an eyebrow. */
  structure: string;
  title: string;
  pitch: string;
}

export interface FinaleProps {
  /** Drives the win vs. death copy + flourish. */
  outcome: "win" | "death";
  storyName: string;

  /** Scenario theme tokens (mirrors ShareModal/BookReader resolution). */
  accent: string;
  accentMuted: string;
  ink: string;
  inkMuted: string;
  paper: string;
  paperEdge: string;
  fontFamily: string;

  /** Open this story's book (chapters view). */
  onReadBook: () => void;
  /** Open the fork-from-a-point flow (the story's BookReader, where the
   *  per-chapter "Continue from chapter N" action lives). */
  onContinueFromPoint: () => void;
  /** Open the existing ShareModal (copy link / native share / publish). */
  onShare: () => void;
  /** Restart THIS story (preserving identity) — the existing restart path. */
  onRestart: () => void;
  /** Start a brand-new story (scenario picker / story switcher). */
  onNewStory: () => void;

  // ── Phase 3 seam — "next book" cards ──────────────────────────────────
  /** When absent/empty the sequel section renders nothing. Win-only. */
  sequelOptions?: SequelOption[];
  /** Called with the chosen option id. 2a passes a no-op / "coming soon". */
  onBeginSequel?: (optionId: string) => void;

  // ── Phase 2b seam — richer share text ─────────────────────────────────
  /** A generated excerpt/foreword/summary used as the FB + copy share text
   *  when present; falls back to the story title otherwise. */
  shareBlurb?: string;
  /** The canonical public share URL, if the story is already published.
   *  When present, the Facebook button is enabled and deep-links to it.
   *  When absent, the FB button routes through onShare() (publish first). */
  shareUrl?: string;
}

export default function Finale(props: FinaleProps): JSX.Element {
  const { t } = useTranslation(["game", "common"]);
  const isWin = props.outcome === "win";
  const [busyAction, setBusyAction] = useState<string | null>(null);

  const sequels = isWin ? props.sequelOptions ?? [] : [];

  const handleFacebookShare = useCallback(() => {
    // If we don't yet have a public URL, route through the share flow so the
    // author can publish first; the FB intent needs a real, reachable URL.
    if (!props.shareUrl) {
      props.onShare();
      return;
    }
    const intent = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      props.shareUrl,
    )}`;
    window.open(intent, "_blank", "noopener,noreferrer");
  }, [props]);

  const runAction = useCallback(
    (key: string, fn: () => void) => () => {
      setBusyAction(key);
      try {
        fn();
      } finally {
        // The callbacks open overlays / navigate synchronously; clear the
        // pressed state on the next tick so the button doesn't stick.
        window.setTimeout(() => setBusyAction(null), 400);
      }
    },
    [],
  );

  // ── Styles ──────────────────────────────────────────────────────────
  const card: CSSProperties = {
    margin: "2.4em 0 1.2em",
    padding: "28px 28px 24px",
    border: `1px solid ${props.accentMuted}`,
    background: props.paper,
    color: props.ink,
    fontFamily: props.fontFamily,
    borderRadius: 4,
    boxShadow: isWin
      ? `0 0 48px -16px ${props.accentMuted}`
      : "0 12px 40px -18px rgba(0,0,0,0.7)",
    animation: "fadein 0.6s ease-out",
  };
  const flourish: CSSProperties = {
    textAlign: "center",
    fontSize: "1.5rem",
    color: isWin ? props.accent : props.inkMuted,
    letterSpacing: "0.06em",
    marginBottom: 6,
    fontStyle: "italic",
  };
  const subline: CSSProperties = {
    textAlign: "center",
    fontSize: "0.95rem",
    color: props.inkMuted,
    fontStyle: "italic",
    marginBottom: 22,
  };
  const sectionTitle: CSSProperties = {
    margin: "22px 0 10px",
    fontSize: "0.7rem",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    color: props.inkMuted,
    fontFamily: "'JetBrains Mono', monospace",
  };
  const primaryBtn: CSSProperties = {
    width: "100%",
    background: "transparent",
    color: props.accent,
    border: `1px solid ${props.accentMuted}`,
    padding: "12px 16px",
    fontSize: "0.95rem",
    cursor: "pointer",
    borderRadius: 2,
    fontFamily: "inherit",
    marginBottom: 10,
    textAlign: "left",
    transition: "background 0.18s, opacity 0.18s",
  };
  const ghostBtn: CSSProperties = {
    ...primaryBtn,
    color: props.inkMuted,
    borderColor: props.paperEdge,
  };
  const benefitLine: CSSProperties = {
    fontSize: "0.86rem",
    color: props.inkMuted,
    fontStyle: "italic",
    margin: "0 0 10px",
    lineHeight: 1.5,
  };
  const shareRow: CSSProperties = {
    display: "flex",
    gap: 10,
  };
  const shareBtnHalf: CSSProperties = {
    flex: 1,
    background: "transparent",
    color: props.accent,
    border: `1px solid ${props.accentMuted}`,
    padding: "10px 12px",
    fontSize: "0.88rem",
    cursor: "pointer",
    borderRadius: 2,
    fontFamily: "inherit",
    textAlign: "center",
    transition: "background 0.18s",
  };
  const fbBtnHalf: CSSProperties = {
    ...shareBtnHalf,
    color: "#a8c0e8",
    borderColor: "rgba(168,192,232,0.4)",
  };
  const restartRow: CSSProperties = {
    display: "flex",
    gap: 10,
    marginTop: 8,
  };
  const restartBtn: CSSProperties = {
    flex: 1,
    background: "transparent",
    color: props.inkMuted,
    border: `1px solid ${props.paperEdge}`,
    padding: "10px 12px",
    fontSize: "0.84rem",
    cursor: "pointer",
    borderRadius: 2,
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: "0.06em",
    textAlign: "center",
    transition: "color 0.18s",
  };
  const hint: CSSProperties = {
    marginTop: 14,
    fontSize: "0.74rem",
    color: props.inkMuted,
    fontFamily: "'JetBrains Mono', monospace",
    letterSpacing: "0.08em",
    textAlign: "center",
    opacity: 0.7,
  };

  // ── Sequel card styles ────────────────────────────────────────────────
  const sequelCard: CSSProperties = {
    border: `1px solid ${props.paperEdge}`,
    borderRadius: 3,
    padding: "14px 16px",
    marginBottom: 10,
    background: "rgba(0,0,0,0.18)",
  };
  const sequelStructure: CSSProperties = {
    fontSize: "0.66rem",
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    color: props.accent,
    fontFamily: "'JetBrains Mono', monospace",
    marginBottom: 4,
  };
  const sequelTitle: CSSProperties = {
    fontSize: "1.05rem",
    color: props.ink,
    marginBottom: 4,
  };
  const sequelPitch: CSSProperties = {
    fontSize: "0.86rem",
    color: props.inkMuted,
    fontStyle: "italic",
    lineHeight: 1.5,
    marginBottom: 10,
  };
  const sequelBtn: CSSProperties = {
    background: "transparent",
    color: props.accent,
    border: `1px solid ${props.accentMuted}`,
    padding: "7px 14px",
    fontSize: "0.82rem",
    cursor: "pointer",
    borderRadius: 2,
    fontFamily: "inherit",
  };

  return (
    <div style={card} role="group" aria-label={t("game:finale.ariaLabel")}>
      <div style={flourish}>
        {isWin ? t("game:finale.winFlourish") : t("game:finale.deathFlourish")}
      </div>
      <div style={subline}>
        {isWin ? t("game:finale.winSubline") : t("game:finale.deathSubline")}
      </div>

      {/* 2. Read your book */}
      <button
        type="button"
        style={{ ...primaryBtn, opacity: busyAction === "read" ? 0.6 : 1 }}
        onClick={runAction("read", props.onReadBook)}
      >
        {t("game:finale.readBook")}
      </button>

      {/* 3. Sequel cards (win-only; renders nothing when empty) */}
      {sequels.length > 0 && (
        <>
          <div style={sectionTitle}>{t("game:finale.sequelsTitle")}</div>
          {sequels.map((opt) => (
            <div key={opt.id} style={sequelCard}>
              <div style={sequelStructure}>{opt.structure}</div>
              <div style={sequelTitle}>{opt.title}</div>
              <div style={sequelPitch}>{opt.pitch}</div>
              <button
                type="button"
                style={sequelBtn}
                onClick={() => props.onBeginSequel?.(opt.id)}
              >
                {t("game:finale.beginSequel")}
              </button>
            </div>
          ))}
        </>
      )}

      {/* 4. Continue from a different point */}
      <div style={sectionTitle}>{t("game:finale.continueTitle")}</div>
      <button
        type="button"
        style={{ ...ghostBtn, opacity: busyAction === "fork" ? 0.6 : 1 }}
        onClick={runAction("fork", props.onContinueFromPoint)}
      >
        {t("game:finale.continueFromPoint")}
      </button>

      {/* 5. Share your story */}
      <div style={sectionTitle}>{t("game:finale.shareTitle")}</div>
      <p style={benefitLine}>{t("game:finale.shareBenefit")}</p>
      <div style={shareRow}>
        <button type="button" style={shareBtnHalf} onClick={props.onShare}>
          {t("game:finale.shareStory")}
        </button>
        <button
          type="button"
          style={fbBtnHalf}
          onClick={handleFacebookShare}
          aria-label={t("game:finale.shareFacebookAria")}
        >
          {t("game:finale.shareFacebook")}
        </button>
      </div>

      {/* 6. Restart / New story */}
      <div style={restartRow}>
        <button
          type="button"
          style={restartBtn}
          onClick={runAction("restart", props.onRestart)}
        >
          {t("game:finale.restart")}
        </button>
        <button
          type="button"
          style={restartBtn}
          onClick={runAction("new", props.onNewStory)}
        >
          {t("game:finale.newStory")}
        </button>
      </div>
      <div style={hint}>{t("game:finale.restartHint")}</div>
    </div>
  );
}
